import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { NoTranscription } from '@frontend/assets';
import {
  CallAnalyzeStatus,
  CallEmptyState,
  CallFlagsData,
  type CallInsightBaseProps,
  callStatusUtils,
  CallSummary,
  CallTranscript,
  CIBannerEnum,
  CIBannerPriorityGroupEnum,
  generateTrackingId,
  SummarySyncChip,
  useCallIntelPmsSyncConfig,
} from '@frontend/call-intel-core';
import { Divider } from '@frontend/divider';
import { CallInsightSummaryWritebackBanner } from '@frontend/high-value-adoption';
import { useTranslation } from '@frontend/i18n';
import { IconName } from '@frontend/icons';
import { PriorityRendererItemWrapper } from '@frontend/priority-renderer';
import { theme } from '@frontend/theme';
import { Accordion, ContentSwitcher, contextFactory } from '@frontend/design-system';
import { CallInsightAccordionType } from '../../types';

// ————————— Types & Utils —————————
type ConversationSection = 'summary' | 'transcript';

interface ConversationAccordionContextValue extends CallInsightBaseProps {
  callFlags?: CallFlagsData;
  hasCallIntelAccess: boolean;
  showTranscriptOnly: boolean;
}
interface ConversationAccordionRootProps extends Omit<ConversationAccordionContextValue, 'showTranscriptOnly'> {
  value?: CallInsightAccordionType | null;
}
interface ConversationAccordionNamespace extends FC<PropsWithChildren<ConversationAccordionRootProps>> {
  Header: typeof ConversationAccordionHeader;
  Body: typeof ConversationAccordionBody;
}

const hasHighlightedCitation = (highlightedCitation?: CallIntelTypes.HighlightedCitation) =>
  !!highlightedCitation?.text && !!highlightedCitation?.time;

const getDefaultSection = ({
  highlightedCitation,
  showTranscriptOnly,
}: {
  highlightedCitation?: CallIntelTypes.HighlightedCitation;
  showTranscriptOnly?: boolean;
}): ConversationSection => {
  if (showTranscriptOnly || hasHighlightedCitation(highlightedCitation)) {
    return 'transcript';
  }

  return 'summary';
};

const checkHasTranscriptOnly = (callMetadata?: CallIntelTypes.CallMetadata) =>
  !!callMetadata?.transcript && !callMetadata?.summary;

// ————————— Context Factory —————————

const [ConversationAccordionContext, useConversationAccordionContext] = contextFactory<
  ConversationAccordionContextValue & Required<Pick<ConversationAccordionContextValue, 'callFlags'>>
>('useConversationAccordionContext must be used within ConversationAccordion');

// ————————— Conversation Accordion —————————

export const ConversationAccordion: ConversationAccordionNamespace = ({ children, value, ...props }) => {
  const callFlags = props.callFlags ?? callStatusUtils.getCallFlags(props.call);
  const showTranscriptOnly = checkHasTranscriptOnly(props.callMetadata);

  if (!props.hasCallIntelAccess) {
    return null;
  }

  return (
    <ConversationAccordionContext.Provider value={{ ...props, showTranscriptOnly, callFlags }}>
      <Accordion.Item
        value={value || 'conversation'}
        data-testid='conversation-accordion'
        trackingId={generateTrackingId({
          prefix: 'call-insight-panel',
          component: 'conversation-accordion',
          isDemoAccount: props.isDemoAccount,
        })}
      >
        {children}
      </Accordion.Item>
    </ConversationAccordionContext.Provider>
  );
};

const ConversationAccordionHeader: FC<{ title?: string; iconName?: IconName }> = ({ title, iconName }) => {
  const { t } = useTranslation('call-insight-panel');
  const { showTranscriptOnly } = useConversationAccordionContext();
  const defaultTitle = showTranscriptOnly ? t('Transcript') : t('Conversation');
  const headerTitle = title || defaultTitle;
  return (
    <Accordion.Header title={iconName ? undefined : headerTitle}>
      {iconName && <Accordion.Header.Icon icon={iconName} title={headerTitle} />}
    </Accordion.Header>
  );
};

const ConversationAccordionBody: FC<
  PropsWithChildren<{ highlightedCitation?: CallIntelTypes.HighlightedCitation }>
> = ({ children, highlightedCitation }) => {
  const { t } = useTranslation('call-insight-panel');
  const { call, callFlags, callMetadata, isDemoAccount, isLoading, showTranscriptOnly } =
    useConversationAccordionContext();
  const [selectedSection, setSelectedSection] = useState<ConversationSection>(() =>
    getDefaultSection({ highlightedCitation, showTranscriptOnly })
  );

  useEffect(() => {
    if (showTranscriptOnly || hasHighlightedCitation(highlightedCitation)) {
      setSelectedSection('transcript');
    }
  }, [showTranscriptOnly, highlightedCitation]);

  if (!callFlags.isProcessingCall && !callFlags.isAnalyzed) {
    return (
      <Accordion.Body css={styles.accordionBody}>
        <CallAnalyzeStatus {...callFlags} type='summary' styles={{ margin: 0 }} />
        <CallEmptyState
          mainText={t('No Summary or Transcript available.')}
          subText={t('Please review the recording.')}
          svg={NoTranscription}
        />
      </Accordion.Body>
    );
  }

  return (
    <Accordion.Body css={styles.accordionBody}>
      {!showTranscriptOnly && (
        <div data-testid='conversation-content-switcher'>
          <ContentSwitcher<ConversationSection>
            key={selectedSection}
            defaultValue={selectedSection}
            onValueChange={setSelectedSection}
          >
            <ContentSwitcher.Option
              iconName='document-filled-small'
              label={t('Summary')}
              trackingId={generateTrackingId({
                prefix: 'call-insight-panel',
                component: 'conversation-accordion',
                context: 'summary-switch',
                isDemoAccount,
              })}
              value='summary'
            />
            <ContentSwitcher.Option
              iconName='list-small'
              label={t('Transcript')}
              trackingId={generateTrackingId({
                prefix: 'call-insight-panel',
                component: 'conversation-accordion',
                context: 'transcript-switch',
                isDemoAccount,
              })}
              value='transcript'
            />
          </ContentSwitcher>
        </div>
      )}

      <div
        data-testid='conversation-transcript-section'
        css={[styles.contentContainer, selectedSection !== 'transcript' && styles.contentHidden]}
      >
        <CallTranscript
          call={call}
          callMetadata={callMetadata}
          isLoading={isLoading}
          isDemoAccount={isDemoAccount}
          highlightedCitation={highlightedCitation}
          containerStyles={{ maxHeight: 300 }}
        />
      </div>

      <div
        data-testid='conversation-summary-section'
        css={[styles.contentContainer, selectedSection !== 'summary' && styles.contentHidden]}
      >
        <CallSummary call={call} callMetadata={callMetadata} isLoading={isLoading} isDemoAccount={isDemoAccount}>
          <SummarySyncAction />
        </CallSummary>
      </div>

      {children}
    </Accordion.Body>
  );
};

const SummarySyncAction: FC = () => {
  const { call, callMetadata, callFlags, isDemoAccount } = useConversationAccordionContext();
  const { isPrivateCall } = callFlags ?? callStatusUtils.getCallFlags(call);

  const { showSummarySync } = useCallIntelPmsSyncConfig({
    call,
    isDemo: isDemoAccount,
  });

  const summarySyncEligible = !isPrivateCall && showSummarySync && !!callMetadata?.summary;

  if (!summarySyncEligible) {
    return null;
  }

  return (
    <div css={styles.summarySyncAction} data-testid='summary-sync-action'>
      <Divider margin={0} color={theme.colors.neutral20} />
      <PriorityRendererItemWrapper<typeof CallInsightSummaryWritebackBanner>
        itemName={CIBannerEnum.PMS_WRITEBACK_CALL_INSIGHTS}
        groupName={CIBannerPriorityGroupEnum.CALL_INSIGHTS_PANEL}
        closeFnProp={'onDismiss'}
      >
        <CallInsightSummaryWritebackBanner locationId={call?.locationId} isDemo={isDemoAccount} />
      </PriorityRendererItemWrapper>
      <div css={styles.summarySyncActionRow}>
        <SummarySyncChip isDemo={isDemoAccount} callRecord={call} />
      </div>
    </div>
  );
};

ConversationAccordion.Header = ConversationAccordionHeader;
ConversationAccordion.Body = ConversationAccordionBody;

const styles = {
  accordionBody: css`
    padding: ${theme.spacing(2)};
    display: flex;
    flex-direction: column;
    overflow: auto;
    gap: ${theme.spacing(2)};
  `,
  contentContainer: css`
    display: flex;
    flex-direction: column;
    overflow: auto;
  `,
  contentHidden: css`
    display: none;
  `,
  summarySyncAction: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
  summarySyncActionRow: css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${theme.spacing(1)};
  `,
};
